import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import API_CONFIG from '../config/api';
import { OrdersData, ChartDataItem, OrderUser } from '../types/api';
import { requestManager } from '../utils/requestManager';
import { cacheEventManager } from '../utils/cacheEventManager';

interface OrdersProps {
    timeRange: string;
}

const Orders: React.FC<OrdersProps> = ({ timeRange }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [ordersData, setOrdersData] = useState<OrdersData | null>(null);

    // State for collapsible sections
    const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({
        pendingUsers: false,
        paidUsers: false,
        orderCharts: false,
        regionalBreakdown: false,
        plansBreakdown: false,
        summaryStatistics: false
    });

    // Toggle function for collapsible sections
    const toggleSection = (sectionKey: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // API Helper Function with request deduplication
    const apiCall = async (endpoint: string) => {
        try {
            const url = `${API_CONFIG.BASE_URL}${endpoint}?period=${timeRange}`;
            const data = await requestManager.makeRequest(url);

            if (!data.success) throw new Error(data.message || 'API call failed');
            return data.data;
        } catch (error) {
            console.error(`API Error for ${endpoint}:`, error);
            throw error;
        }
    };

    const fetchOrdersData = async () => {
        try {
            setLoading(true);
            const data = await apiCall(API_CONFIG.ENDPOINTS.ANALYTICS.ORDERS);
            setOrdersData(data);
        } catch (error) {
            console.error('Error fetching orders data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrdersData();
    }, [timeRange]);

    // Listen for cache clear events to auto-refresh data
    useEffect(() => {
        const unsubscribe = cacheEventManager.subscribe(() => {
            fetchOrdersData();
        });

        return unsubscribe;
    }, [timeRange]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading orders data...</p>
                </div>
            </div>
        );
    }

    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

    // Prepare data for charts
    const orderStatusData = ordersData ? [
        { name: 'Paid Orders', value: ordersData.total_paid || 0, color: '#10B981' },
        { name: 'Pending Orders', value: ordersData.total_pending || 0, color: '#F59E0B' }
    ] : [];

    // Prepare order distribution by region data
    const orderDistributionData = ordersData ? [
        { name: 'Indian', value: ordersData.total_indian_paid || 0, color: '#3B82F6' },
        { name: 'Foreigner', value: ordersData.total_foreigner_paid || 0, color: '#10B981' }
    ].filter(item => item.value > 0) : [];

    // Prepare data for order type breakdown
    const orderTypeData = ordersData ? [
        { name: 'Pro Orders', value: ordersData.total_pro || 0, color: '#8B5CF6' },
        { name: 'Plus Orders', value: ordersData.total_plus || 0, color: '#3B82F6' },
        { name: 'Topup Orders', value: ordersData.total_topup || 0, color: '#10B981' }
    ].filter(item => item.value > 0) : [];

    // Prepare Indian Orders Data - try multiple data structures
    const indianOrdersData = (() => {
        // Try orders_breakdown structure first
        if (ordersData?.orders_breakdown?.indian) {
            return [
                { category: 'Pro Monthly', paid: ordersData.orders_breakdown.indian.paid.pro.monthly || 0, pending: ordersData.orders_breakdown.indian.pending.pro.monthly || 0 },
                { category: 'Pro 4M', paid: ordersData.orders_breakdown.indian.paid.pro.four_months || 0, pending: ordersData.orders_breakdown.indian.pending.pro.four_months || 0 },
                { category: 'Pro Yearly', paid: ordersData.orders_breakdown.indian.paid.pro.yearly || 0, pending: ordersData.orders_breakdown.indian.pending.pro.yearly || 0 },
                { category: 'Plus Monthly', paid: ordersData.orders_breakdown.indian.paid.plus.monthly || 0, pending: ordersData.orders_breakdown.indian.pending.plus.monthly || 0 },
                { category: 'Plus 4M', paid: ordersData.orders_breakdown.indian.paid.plus.four_months || 0, pending: ordersData.orders_breakdown.indian.pending.plus.four_months || 0 },
                { category: 'Plus Yearly', paid: ordersData.orders_breakdown.indian.paid.plus.yearly || 0, pending: ordersData.orders_breakdown.indian.pending.plus.yearly || 0 },
                { category: 'Topup Monthly', paid: ordersData.orders_breakdown.indian.paid.topup.monthly || 0, pending: ordersData.orders_breakdown.indian.pending.topup.monthly || 0 },
                { category: 'Topup 4M', paid: ordersData.orders_breakdown.indian.paid.topup.four_months || 0, pending: ordersData.orders_breakdown.indian.pending.topup.four_months || 0 },
                { category: 'Topup Yearly', paid: ordersData.orders_breakdown.indian.paid.topup.yearly || 0, pending: ordersData.orders_breakdown.indian.pending.topup.yearly || 0 }
            ];
        }

        // Try direct structure (based on your API response)
        if (ordersData?.indian) {
            return [
                { category: 'Pro Monthly', paid: ordersData.indian.paid.pro.monthly || 0, pending: ordersData.indian.pending.pro.monthly || 0 },
                { category: 'Pro 4M', paid: ordersData.indian.paid.pro.four_months || 0, pending: ordersData.indian.pending.pro.four_months || 0 },
                { category: 'Pro Yearly', paid: ordersData.indian.paid.pro.yearly || 0, pending: ordersData.indian.pending.pro.yearly || 0 },
                { category: 'Plus Monthly', paid: ordersData.indian.paid.plus.monthly || 0, pending: ordersData.indian.pending.plus.monthly || 0 },
                { category: 'Plus 4M', paid: ordersData.indian.paid.plus.four_months || 0, pending: ordersData.indian.pending.plus.four_months || 0 },
                { category: 'Plus Yearly', paid: ordersData.indian.paid.plus.yearly || 0, pending: ordersData.indian.pending.plus.yearly || 0 },
                { category: 'Topup Monthly', paid: ordersData.indian.paid.topup.monthly || 0, pending: ordersData.indian.pending.topup.monthly || 0 },
                { category: 'Topup 4M', paid: ordersData.indian.paid.topup.four_months || 0, pending: ordersData.indian.pending.topup.four_months || 0 },
                { category: 'Topup Yearly', paid: ordersData.indian.paid.topup.yearly || 0, pending: ordersData.indian.pending.topup.yearly || 0 }
            ];
        }

        // Fallback: Use direct totals if detailed breakdown is not available
        return [
            { category: 'Pro Plans', paid: ordersData?.total_pro || 0, pending: 0 },
            { category: 'Plus Plans', paid: ordersData?.total_plus || 0, pending: 0 },
            { category: 'Topup Plans', paid: ordersData?.total_topup || 0, pending: 0 }
        ];
    })();

    // Prepare Foreigner Orders Data - try multiple data structures
    const foreignerOrdersData = (() => {
        // Try orders_breakdown structure first
        if (ordersData?.orders_breakdown?.foreigner) {
            return [
                { category: 'Pro Monthly', paid: ordersData.orders_breakdown.foreigner.paid.pro.monthly || 0, pending: ordersData.orders_breakdown.foreigner.pending.pro.monthly || 0 },
                { category: 'Pro 4M', paid: ordersData.orders_breakdown.foreigner.paid.pro.four_months || 0, pending: ordersData.orders_breakdown.foreigner.pending.pro.four_months || 0 },
                { category: 'Pro Yearly', paid: ordersData.orders_breakdown.foreigner.paid.pro.yearly || 0, pending: ordersData.orders_breakdown.foreigner.pending.pro.yearly || 0 },
                { category: 'Plus Monthly', paid: ordersData.orders_breakdown.foreigner.paid.plus.monthly || 0, pending: ordersData.orders_breakdown.foreigner.pending.plus.monthly || 0 },
                { category: 'Plus 4M', paid: ordersData.orders_breakdown.foreigner.paid.plus.four_months || 0, pending: ordersData.orders_breakdown.foreigner.pending.plus.four_months || 0 },
                { category: 'Plus Yearly', paid: ordersData.orders_breakdown.foreigner.paid.plus.yearly || 0, pending: ordersData.orders_breakdown.foreigner.pending.plus.yearly || 0 },
                { category: 'Topup Monthly', paid: ordersData.orders_breakdown.foreigner.paid.topup.monthly || 0, pending: ordersData.orders_breakdown.foreigner.pending.topup.monthly || 0 },
                { category: 'Topup 4M', paid: ordersData.orders_breakdown.foreigner.paid.topup.four_months || 0, pending: ordersData.orders_breakdown.foreigner.pending.topup.four_months || 0 },
                { category: 'Topup Yearly', paid: ordersData.orders_breakdown.foreigner.paid.topup.yearly || 0, pending: ordersData.orders_breakdown.foreigner.pending.topup.yearly || 0 }
            ];
        }

        // Try direct structure (based on your API response)
        if (ordersData?.foreigner) {
            return [
                { category: 'Pro Monthly', paid: ordersData.foreigner.paid.pro.monthly || 0, pending: ordersData.foreigner.pending.pro.monthly || 0 },
                { category: 'Pro 4M', paid: ordersData.foreigner.paid.pro.four_months || 0, pending: ordersData.foreigner.pending.pro.four_months || 0 },
                { category: 'Pro Yearly', paid: ordersData.foreigner.paid.pro.yearly || 0, pending: ordersData.foreigner.pending.pro.yearly || 0 },
                { category: 'Plus Monthly', paid: ordersData.foreigner.paid.plus.monthly || 0, pending: ordersData.foreigner.pending.plus.monthly || 0 },
                { category: 'Plus 4M', paid: ordersData.foreigner.paid.plus.four_months || 0, pending: ordersData.foreigner.pending.plus.four_months || 0 },
                { category: 'Plus Yearly', paid: ordersData.foreigner.paid.plus.yearly || 0, pending: ordersData.foreigner.pending.plus.yearly || 0 },
                { category: 'Topup Monthly', paid: ordersData.foreigner.paid.topup.monthly || 0, pending: ordersData.foreigner.pending.topup.monthly || 0 },
                { category: 'Topup 4M', paid: ordersData.foreigner.paid.topup.four_months || 0, pending: ordersData.foreigner.pending.topup.four_months || 0 },
                { category: 'Topup Yearly', paid: ordersData.foreigner.paid.topup.yearly || 0, pending: ordersData.foreigner.pending.topup.yearly || 0 }
            ];
        }

        // Fallback: Use direct totals if detailed breakdown is not available
        return [
            { category: 'Pro Plans', paid: ordersData?.total_pro || 0, pending: 0 },
            { category: 'Plus Plans', paid: ordersData?.total_plus || 0, pending: 0 },
            { category: 'Topup Plans', paid: ordersData?.total_topup || 0, pending: 0 }
        ];
    })();


    // Collapsible Header Component
    const CollapsibleHeader: React.FC<{
        title: string;
        count?: number;
        color: string;
        isCollapsed: boolean;
        onToggle: () => void;
    }> = ({ title, count, color, isCollapsed, onToggle }) => (
        <div
            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 -m-2 transition-colors"
            onClick={onToggle}
        >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                <div className={`w-4 h-4 ${color} rounded-full mr-2`}></div>
                {title} {count !== undefined && `(${count})`}
            </h3>
            <div className="flex items-center">
                <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''
                        }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>
    );

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Content */}
            <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
                {/* Summary Cards - Single Row Layout */}
                <div className="grid grid-cols-6 gap-4 mb-8">
                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Total Orders</p>
                        <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">
                            {ordersData?.total_orders?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Paid Orders</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                            {ordersData?.total_paid?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Pending Orders</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">
                            {ordersData?.total_pending?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Revenue (INR)</p>
                        <p className="text-xl font-bold text-orange-600 mt-1">
                            ₹{ordersData?.revenue_in_inr?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Revenue (USD)</p>
                        <p className="text-xl font-bold text-orange-600 mt-1">
                            ${ordersData?.revenue_in_usd?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Top Plan</p>
                        <p className="text-lg font-bold text-cyan-600 mt-1 capitalize">
                            {ordersData?.most_purchased_plan?.replace(/_/g, ' ') || 'N/A'}
                        </p>
                    </div>
                </div>

                {/* Pending Users Details - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Pending Users Details"
                        isCollapsed={collapsedSections.pendingUsers}
                        onToggle={() => toggleSection('pendingUsers')}
                        color="bg-amber-500"
                    />

                    {!collapsedSections.pendingUsers && (
                        <div className="flex gap-6 mt-6">
                            {/* Indian Pending Users - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                                    Indian Pending Users ({ordersData?.pending_indian_users?.length || 0})
                                </h4>
                                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Order Time
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {ordersData?.pending_indian_users
                                                ?.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                                                ?.map((user: OrderUser, index: number) => (
                                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                    <div className="h-10 w-10 rounded-full bg-amber-400 flex items-center justify-center text-white font-semibold text-sm">
                                                                        {(user.email || 'U').charAt(0).toUpperCase()}
                                                                    </div>
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {user.email || 'N/A'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {user.user_id || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {user.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                            {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'N/A'}
                                                        </td>
                                                    </tr>
                                                )) || []}
                                        </tbody>
                                    </table>
                                </div>
                                {(!ordersData?.pending_indian_users?.length) && (
                                    <div className="text-center py-8">
                                        <div className="text-gray-500 dark:text-gray-400">
                                            No pending Indian users found.
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Foreigner Pending Users - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                                    Foreigner Pending Users ({ordersData?.pending_foreigner_users?.length || 0})
                                </h4>
                                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Order Time
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {ordersData?.pending_foreigner_users
                                                ?.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                                                ?.map((user: OrderUser, index: number) => (
                                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                    <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold text-sm">
                                                                        {(user.email || 'U').charAt(0).toUpperCase()}
                                                                    </div>
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {user.email || 'N/A'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {user.user_id || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {user.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                            {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'N/A'}
                                                        </td>
                                                    </tr>
                                                )) || []}
                                        </tbody>
                                    </table>
                                </div>
                                {(!ordersData?.pending_foreigner_users?.length) && (
                                    <div className="text-center py-8">
                                        <div className="text-gray-500 dark:text-gray-400">
                                            No pending foreigner users found.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Paid Users Details - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Paid Users Details"
                        isCollapsed={collapsedSections.paidUsers}
                        onToggle={() => toggleSection('paidUsers')}
                        color="bg-green-500"
                    />

                    {!collapsedSections.paidUsers && (
                        <div className="flex gap-6 mt-6">
                            {/* Indian Paid Users - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                    Indian Paid Users ({ordersData?.paid_indian_users?.length || 0})
                                </h4>
                                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Order Time
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {ordersData?.paid_indian_users
                                                ?.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                                                ?.map((user: OrderUser, index: number) => (
                                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                    <div className="h-10 w-10 rounded-full bg-green-400 flex items-center justify-center text-white font-semibold text-sm">
                                                                        {(user.email || 'U').charAt(0).toUpperCase()}
                                                                    </div>
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {user.email || 'N/A'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {user.user_id || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {user.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                            {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'N/A'}
                                                        </td>
                                                    </tr>
                                                )) || []}
                                        </tbody>
                                    </table>
                                </div>
                                {(!ordersData?.paid_indian_users?.length) && (
                                    <div className="text-center py-8">
                                        <div className="text-gray-500 dark:text-gray-400">
                                            No paid Indian users found.
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Foreigner Paid Users - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                    Foreigner Paid Users ({ordersData?.paid_foreigner_users?.length || 0})
                                </h4>
                                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Order Time
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {ordersData?.paid_foreigner_users
                                                ?.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                                                ?.map((user: OrderUser, index: number) => (
                                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                    <div className="h-10 w-10 rounded-full bg-blue-400 flex items-center justify-center text-white font-semibold text-sm">
                                                                        {(user.email || 'U').charAt(0).toUpperCase()}
                                                                    </div>
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {user.email || 'N/A'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {user.user_id || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {user.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                            {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : 'N/A'}
                                                        </td>
                                                    </tr>
                                                )) || []}
                                        </tbody>
                                    </table>
                                </div>
                                {(!ordersData?.paid_foreigner_users?.length) && (
                                    <div className="text-center py-8">
                                        <div className="text-gray-500 dark:text-gray-400">
                                            No paid foreigner users found.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Order Charts - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Order Charts"
                        isCollapsed={collapsedSections.orderCharts}
                        onToggle={() => toggleSection('orderCharts')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.orderCharts && (
                        <div className="flex gap-6 mt-6">
                            {/* Order Distribution by Region - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Order Distribution by Region</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={orderDistributionData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                                            >
                                                {orderDistributionData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Type of Orders Breakdown - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Type of Orders Breakdown</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={orderTypeData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                                            >
                                                {orderTypeData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Regional Breakdown - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Regional Breakdown"
                        isCollapsed={collapsedSections.regionalBreakdown}
                        onToggle={() => toggleSection('regionalBreakdown')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.regionalBreakdown && (
                        <div className="flex gap-6 mt-6">
                            {/* Indian Orders - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                                    Indian Orders Breakdown
                                </h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={indianOrdersData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="category"
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                                tick={{ fontSize: 10 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <Legend />
                                            <Bar dataKey="paid" stackId="a" fill="#10B981" name="Paid" />
                                            <Bar dataKey="pending" stackId="a" fill="#F59E0B" name="Pending" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Foreigner Orders - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                    Foreigner Orders Breakdown
                                </h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={foreignerOrdersData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="category"
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                                tick={{ fontSize: 10 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <Legend />
                                            <Bar dataKey="paid" stackId="a" fill="#10B981" name="Paid" />
                                            <Bar dataKey="pending" stackId="a" fill="#F59E0B" name="Pending" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* All Plans Breakdown - Single Row Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="All Plans Breakdown"
                        isCollapsed={collapsedSections.plansBreakdown}
                        onToggle={() => toggleSection('plansBreakdown')}
                        color="bg-cyan-500"
                    />

                    {!collapsedSections.plansBreakdown && (
                        <div className="flex gap-6 mt-6">
                            {/* Pro Plans Breakdown - Left Third */}
                            <div className="w-1/3">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                                    Pro Plans Breakdown
                                </h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Monthly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_pro_monthly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">4 Months</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_pro_four_months || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Yearly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_pro_yearly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-orange-100 dark:bg-orange-900 rounded-lg border-t-2 border-orange-500">
                                        <span className="text-gray-800 dark:text-white font-semibold">Total Pro</span>
                                        <span className="text-xl font-bold text-orange-600">
                                            {ordersData?.total_pro || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Plus Plans Breakdown - Middle Third */}
                            <div className="w-1/3">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                    Plus Plans Breakdown
                                </h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Monthly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_plus_monthly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">4 Months</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_plus_four_months || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Yearly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_plus_yearly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-blue-100 dark:bg-blue-900 rounded-lg border-t-2 border-blue-500">
                                        <span className="text-gray-800 dark:text-white font-semibold">Total Plus</span>
                                        <span className="text-xl font-bold text-blue-600">
                                            {ordersData?.total_plus || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Topup Plans Breakdown - Right Third */}
                            <div className="w-1/3">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                    Topup Plans Breakdown
                                </h4>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Monthly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_topup_monthly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">4 Months</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_topup_four_months || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <span className="text-gray-600 dark:text-gray-400">Yearly</span>
                                        <span className="text-lg font-semibold text-gray-800 dark:text-white">
                                            {ordersData?.total_topup_yearly || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-green-100 dark:bg-green-900 rounded-lg border-t-2 border-green-500">
                                        <span className="text-gray-800 dark:text-white font-semibold">Total Topup</span>
                                        <span className="text-xl font-bold text-green-600">
                                            {ordersData?.total_topup || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Summary Statistics */}
                <div className="glass-card p-6">
                    <CollapsibleHeader
                        title="Order Summary Statistics"
                        isCollapsed={collapsedSections.summaryStatistics}
                        onToggle={() => toggleSection('summaryStatistics')}
                        color="bg-gray-500"
                    />

                    {!collapsedSections.summaryStatistics && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-orange-600">{ordersData?.total_indian_paid || 0}</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Indian Paid</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-orange-600">{ordersData?.total_indian_pending || 0}</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Indian Pending</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">{ordersData?.total_foreigner_paid || 0}</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Foreigner Paid</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">{ordersData?.total_foreigner_pending || 0}</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Foreigner Pending</div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Orders;